import React from "react";
import { useState, useEffect } from "react";
import AuthService from "../services/auth.service";
import api from "../services/api";
import { Link, useParams, useLocation } from "react-router-dom";
import WeightChart2 from "../components/veauHub/WeightChart";
import "./PrintCalfReport.css";
import { describe } from "../constants/diseases";

function PrintCalfReport() {
  const currentUser = AuthService.getCurrentUser();
  const [calf, setCalf] = useState({});
  const [diseases, setDiseases] = useState([]);
  const [vaccines, setVaccines] = useState([]);
  const [weights, setWeights] = useState([]);
  const [milks, setMilks] = useState([]);
  const [weightsChart, setWeightsChart] = useState([]);
  const [GMQ, setGMQ] = useState(0);
  const [totalMilkQuantity, setTotalMilkQuantity] = useState(0);
  let { id } = useParams();

  useEffect(() => {
    //Runs only on the first render
    if (currentUser) {
      // Check if this is for PDF generation
      const isPdfGeneration = localStorage.getItem('isPdfGeneration') === 'true';

      if (isPdfGeneration) {
        // Use preloaded data for PDF generation
        const calfData = JSON.parse(localStorage.getItem('calfData') || '{}');
        const diseasesData = JSON.parse(localStorage.getItem('diseasesData') || '[]');
        const vaccinesData = JSON.parse(localStorage.getItem('vaccinesData') || '[]');
        const weightsData = JSON.parse(localStorage.getItem('weightsData') || '[]');
        const milksData = JSON.parse(localStorage.getItem('milksData') || '[]');
        const totalMilkData = JSON.parse(localStorage.getItem('totalMilkQuantity') || '0');

        setCalf(calfData);
        setDiseases(diseasesData);
        setVaccines(vaccinesData);
        setWeights(weightsData);
        setMilks(milksData);
        setTotalMilkQuantity(totalMilkData);

        // Process weights for chart
        let chartData = [];
        weightsData.forEach((weight) => {
          chartData.push({ x: weight.date, y: weight.weight });
        });
        setWeightsChart(chartData);

        // Clean up localStorage
        localStorage.removeItem('isPdfGeneration');
        localStorage.removeItem('calfData');
        localStorage.removeItem('diseasesData');
        localStorage.removeItem('vaccinesData');
        localStorage.removeItem('weightsData');
        localStorage.removeItem('milksData');
        localStorage.removeItem('totalMilkQuantity');
      } else {
        getCalf();
        getDisease();
        getVaccine();
        getWeights();
        getMilk();
      }
    }
  }, [id]); // Add id as dependency to re-run when calf ID changes

  // If user is not logged in, display a message
  if (!currentUser) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold text-gray-800">User not logged in</h2>
          <p className="text-gray-600">Please log in to view this report.</p>
        </div>
      </div>
    );
  }

  const getCalf = () => {
    api.get("/calfs/" + id)
      .then((response) => {
        const data = response.data;
        setCalf(data);
      })
      .catch((error) => {
        console.error("There was an error creating a calf!", error);
      });
  };

  const getDisease = () => {
    api.get("/health/calf/" + id)
      .then((response) => {
        const data = response.data;
        setDiseases(data);
      })
      .catch((error) => {
        console.error("There was an error creating a calf!", error);
      });
  };

  const getVaccine = () => {
    api.get("/vaccine/calf/" + id)
      .then((response) => {
        const data = response.data;
        setVaccines(data);
      })
      .catch((error) => {
        console.error("There was an error creating a calf!", error);
      });
  };

  const getWeights = () => {
    api.get("/weight/calf/" + id)
      .then((response) => {
        const data = response.data;
        setWeights(data);
        let chartData = [];
        data.forEach((weight) => {
          chartData.push({ x: weight.date, y: weight.weight });
        });
        setWeightsChart(chartData);
      })
      .catch((error) => {
        console.error("There was an error creating a calf!", error);
      });
  };

  const getMilk = () => {
    api.get("/milk/calf/" + id)
      .then((response) => {
        const data = response.data;
        setMilks(data.milk);
        setTotalMilkQuantity(data.totalQuantity);
      })
      .catch((error) => {
        console.error("There was an error getting the milk", error);
      });
  };

  // Header component for each page
  const PageHeader = () => (
    <div className="page-header">
      <img
        src="/logo.png"
        alt="Logo"
        style={{
          width: '40px',
          height: '40px',
          marginRight: '15px'
        }}
      />
      <h1 style={{
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1f2937',
        margin: 0
      }}>
        Rapport Check'veau - {calf?.name || 'N/A'}
      </h1>
    </div>
  );

  // Footer component for each page
  const PageFooter = () => (
    <div className="page-footer">
      <p>Rapport généré le {new Date().toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })} | Check'veau - Système de gestion des veaux</p>
    </div>
  );

  return (
    <div className="print-report-container">
      <div className="page">
        <div className="page-content">
          {/* Calf Information */}
          <div className="section">
            <h2 className="section-title">
              Informations du veau
            </h2>

            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Nom:</span>
                <span className="info-value">{calf?.name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">BDTA:</span>
                <span className="info-value">{calf?.bdta || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sexe:</span>
                <span className="info-value">
                  {calf?.sex === "f" ? "Femelle" : "Mâle"}
                </span>
              </div>
              {calf?.sex === "m" && (
                <div className="info-item">
                  <span className="info-label">Castration:</span>
                  <span className="info-value">
                    {calf?.castration ? "Oui" : "Non"}
                  </span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Date de naissance:</span>
                <span className="info-value">
                  {calf?.birthdate ? new Date(calf.birthdate).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }) : 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Heure de naissance:</span>
                <span className="info-value">{calf?.birthtime || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Lieu de naissance:</span>
                <span className="info-value">{calf?.birthPlace || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Vitalité:</span>
                <span className="info-value">{calf?.vitality || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Déroulement de la naissance:</span>
                <span className="info-value">{calf?.birthtype || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Apport en colostrum:</span>
                <span className="info-value">{calf?.colostrum ? `${calf.colostrum} l` : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Heure de l'apport:</span>
                <span className="info-value">{calf?.apporttime || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Moyen d'apport:</span>
                <span className="info-value">{calf?.colostrumMean || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Source du colostrum:</span>
                <span className="info-value">{calf?.colostrumSource || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Qualité du colostrum:</span>
                <span className="info-value">{calf?.colostrumQuality || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Contenu du colostrum:</span>
                <span className="info-value">
                  {[
                    calf?.colostrumContent_fer && "Fer",
                    calf?.colostrumContent_selenium && "Sélénium",
                    calf?.colostrumContent_locatim && "Locatim",
                  ].filter(Boolean).join(", ") || 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Ecornage:</span>
                <span className="info-value">
                  {calf?.ecornage ? "Oui" : "Non"}
                </span>
              </div>
              {calf?.ecornageDate && (
                <div className="info-item">
                  <span className="info-label">Date d'écornage:</span>
                  <span className="info-value">
                    {new Date(calf.ecornageDate).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="section">
            <h2 className="section-title">
              Performances
            </h2>

            <div className="performance-metrics">
              <div className="metric-item">
                <span className="metric-label">GMQ moyen:</span>
                <span className={`metric-value ${GMQ < 0.75 ? 'warning' : 'good'}`}>
                  {GMQ?.toFixed(3) || '0.000'} kg/jour
                </span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Quantité totale de lait:</span>
                <span className="metric-value">
                  {totalMilkQuantity || 0} L
                </span>
              </div>
            </div>
          </div>

          {/* Weight Chart */}
          <div className="section chart-container">
            <h2 className="section-title">
              Évolution du poids
            </h2>
            <div className="data-list">
              <WeightChart2 weights={weightsChart} setGMQ={setGMQ} isPdfMode={true} />
            </div>
          </div>

          {/* Milk Data Table */}
          <div className="section">
            <h2 className="section-title">
              Données de lait ({milks.length} enregistrements)
            </h2>
            {milks.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Quantité (L)</th>
                    <th>Ad libitum</th>
                  </tr>
                </thead>
                <tbody>
                  {milks.map((milk, index) => (
                    <tr key={milk.pk_milk || index}>
                      <td>
                        {new Date(milk.startDate).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        }) + " - " + new Date(milk.endDate).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="tabular-nums">{milk.quantity} L</td>
                      <td>{milk.ad_libitum ? "Ad libitum" : "--"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#C',
                fontStyle: 'italic',
                padding: '20px'
              }}>
                Aucune donnée de lait disponible
              </div>
            )}
          </div>

          {/* Diseases Table */}
          <div className="section">
            <h2 className="section-title">
              Maladies ({diseases.length})
            </h2>
            {diseases.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Maladie</th>
                    <th>Traitement</th>
                    <th>Période</th>
                  </tr>
                </thead>
                <tbody>
                  {diseases.map((disease, index) => (
                    <tr key={disease.pk_health || index}>
                      <td>{describe(disease)}</td>
                      <td>{disease.treatment}</td>
                      <td>
                        {new Date(disease.start_date).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                        {disease.end_date && (
                          <>
                            <br />
                            <span style={{ color: '#6b7280' }}>
                              jusqu'au {new Date(disease.end_date).toLocaleDateString("fr-FR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
                padding: '20px'
              }}>
                Aucune maladie enregistrée
              </div>
            )}
          </div>

          {/* Vaccines Table */}
          <div className="section">
            <h2 className="section-title">
              Vaccins ({vaccines.length})
            </h2>
            {vaccines.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nom du vaccin</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {vaccines.map((vaccine, index) => (
                    <tr key={vaccine.pk_vaccine || index}>
                      <td>{vaccine.name}</td>
                      <td>
                        {new Date(vaccine.date).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
                padding: '20px'
              }}>
                Aucun vaccin enregistré
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrintCalfReport;
